"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ShieldCheck, CircleCheck as CheckCircle2, Zap, RefreshCw, KeyRound } from 'lucide-react';
interface GuaranteeFeature {
  id: number;
  title: string;
  description: string;
}
const guaranteeFeatures: GuaranteeFeature[] = [{
  id: 1,
  title: "100% Free Notion plan compatible",
  description: "Operates seamlessly on the standard free personal plan without upgrading."
}, {
  id: 2,
  title: "One-click instant duplication",
  description: "Transfer the full structure directly into your private workspace in seconds."
}, {
  id: 3,
  title: "Zero recurring or hidden fees",
  description: "Pay once with no monthly software add-ons, seat costs, or maintenance charges."
}, {
  id: 4,
  title: "Lifetime access & documentation",
  description: "Includes complete video walkthroughs, step-by-step setup guides, and framework updates."
}];
export default function Guarantee() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="friction-free-guarantee" data-nav="light" className="bg-background py-16 md:py-24 text-foreground relative overflow-hidden">
      <div className="container mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.25
      }} className="relative bg-muted border border-border rounded-sm p-8 sm:p-12 md:p-14 shadow-xs">
          {/* Subtle Decorative Orbit Hairline Motif */}
          <div aria-hidden="true" className="pointer-events-none absolute -right-20 -top-20 size-72 rounded-full border border-border/40 opacity-70" />
          <div aria-hidden="true" className="pointer-events-none absolute -right-8 -top-8 size-48 rounded-full border border-border/30 opacity-60" />

          <div className="relative z-10 flex flex-col md:flex-row md:items-start md:justify-between gap-8 md:gap-12 pb-8 border-b border-border">
            <div className="max-w-xl">
              <div className="inline-flex items-center gap-2 mb-4">
                <span className="p-1.5 rounded-sm bg-primary/10 text-primary border border-primary/20">
                  <ShieldCheck className="size-4" strokeWidth={1.5} />
                </span>
                <span className="text-xs font-semibold uppercase tracking-widest text-primary">
                  Friction-Free Guarantee
                </span>
              </div>

              <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl font-medium tracking-tight text-foreground leading-tight">
                Zero recurring fees. Runs on free Notion.
              </h2>

              <p className="mt-4 text-sm sm:text-base text-muted-foreground leading-relaxed">
                The Legacy Expedition Suite is engineered to run on Notion{"'"}s 100% free personal tier. 
                There are no monthly subscriptions, no paid third-party extensions, and no technical installations required.
              </p>
            </div>

            <div className="flex shrink-0 flex-row md:flex-col items-start gap-4 pt-1">
              <div className="flex items-center gap-3 rounded-sm bg-card px-4 py-3 border border-border">
                <KeyRound className="size-4 text-primary shrink-0" strokeWidth={1.5} />
                <div className="text-left">
                  <span className="block text-xs font-semibold text-foreground uppercase tracking-wider">
                    Ownership Model
                  </span>
                  <span className="block text-xs text-muted-foreground">
                    One-time acquisition
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-3 rounded-sm bg-card px-4 py-3 border border-border">
                <Zap className="size-4 text-primary shrink-0" strokeWidth={1.5} />
                <div className="text-left">
                  <span className="block text-xs font-semibold text-foreground uppercase tracking-wider">
                    Deployment
                  </span>
                  <span className="block text-xs text-muted-foreground">
                    Under 2 minutes
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Feature Grid */}
          <div className="relative z-10 grid grid-cols-1 sm:grid-cols-2 gap-6 pt-8">
            {guaranteeFeatures.map((feature, i) => <div key={feature.id} data-index={i} className="flex items-start gap-3.5 group">
                <CheckCircle2 className="size-4.5 text-primary shrink-0 mt-0.5" strokeWidth={1.5} />
                <div>
                  <h3 className="text-sm font-semibold text-foreground leading-snug">
                    {feature.title}
                  </h3>
                  <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </div>)}
          </div>

          {/* Quiet Trust Footnote */}
          <div className="relative z-10 mt-8 pt-6 border-t border-border flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs text-muted-foreground">
            <div className="flex items-center gap-2">
              <RefreshCw className="size-3.5 text-muted-foreground shrink-0" strokeWidth={1.5} />
              <span>Full compatibility verified across Notion web, desktop, iOS, and Android apps.</span>
            </div>
            <span className="font-mono text-2xs tracking-wider uppercase text-muted-foreground/80">
              ARCHITECTURAL STANDARD v2.4
            </span>
          </div>
        </motion.div>
      </div>
    </section>;
}